<template>
  <main role="main" class="py-4">
    <b-container fluid="xl" class="mb-3">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("customerUserAccountList") }}</h4>
        <div>
          <b-button variant="outline-secondary" class="mr-4" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button class="mr-4" variant="outline-secondary" v-b-modal:import-users-from-excel-file-modal>
            <b-icon icon="upload" class="mr-2"></b-icon> {{ $t("importFromFile") }}
          </b-button>
          <b-button class="mr-4" variant="outline-primary" @click="onClickDownload">
            <b-icon icon="download" class="mr-2"></b-icon> {{ $t("export") }}
          </b-button>
          <b-button class="mr-4" variant="outline-primary" v-b-modal:new-customer-user-modal>
            <b-icon icon="plus"></b-icon> {{ $t("createNew") }}
          </b-button>
          <b-button variant="outline-danger" @click="onclickDeleteAllViewer">
            <b-icon icon="trash" class="mr-2"></b-icon> {{ $t("deleteAllViewer") }}
          </b-button>
          
        </div>
      </div>

      <div class="d-flex flex-row align-items-center justify-content-between mb-3">
        <div>
          <b-form class="d-inline-block" inline @submit.prevent="onClickSearch">
            <b-form-input class="mr-2" type="search" minlength="2" maxlength="200" v-model="searchText"
                          placeholder="Tìm theo tên, SĐT, email"
                          style="min-width: 300px;" />
            <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
          </b-form>
        </div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>

      <b-table ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(name)="data">
          {{ data.item.name }}
        </template>
        <template v-slot:cell(actions)="data">
          <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots"/>
            </template>
            <b-dropdown-item-button @click="onClickEditCustomerUser(data.item)">{{ $t("updateInfo") }}</b-dropdown-item-button>
            <b-dropdown-item-button @click="onClickResetPassword(data.item)">{{ $t("resetPassword") }}</b-dropdown-item-button>
            <b-dropdown-item-button v-if="data.item.id !== userId" @click="onClickDeleteUser(data.item)">{{ $t("actionDelete") }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>
    </b-container>

    <b-modal id="new-customer-user-modal" :title="$t('createCustomerUserAccount')" dialog-class="min-width-700" hide-footer no-close-on-backdrop>
      <new-customer-user-modal @submitted="refreshData" />
    </b-modal>

    <b-modal id="edit-customer-user-modal" :title="$t('updateAccountInfo')" hide-footer no-close-on-backdrop>
      <edit-customer-user-modal :editing-user="userToEdit" @submitted="refreshData" />
    </b-modal>

    <b-modal id="reset-password-modal" :title="$t('resetPassword')" hide-footer no-close-on-backdrop>
      <reset-password-modal :target-user="userToEdit" @submitted="refreshData" />
    </b-modal>

    <b-modal id="import-users-from-excel-file-modal" :title="$t('importUserFromFile')" hide-footer no-close-on-backdrop>
      <ImportUsersFromExcelFileModal modal-id="import-users-from-excel-file-modal" @submitted="refreshData" />
    </b-modal>

  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";

  import * as util from "@/service/util";
  import * as userService from "@/service/userService";
  import ResetPasswordModal from "@/components/account/ResetPasswordModal.vue";
  import EditCustomerUserModal from "@/components/customer/user-accounts/EditCustomerUserModal.vue";
  import NewCustomerUserModal from "@/components/customer/user-accounts/NewCustomerUserModal.vue";
  import ImportUsersFromExcelFileModal from '@/components/customer/user-accounts/ImportUsersFromExcelFileModal.vue';
  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import * as customerService from "@/service/customerService";


  @Component({
    components: { ImportUsersFromExcelFileModal, NewCustomerUserModal, EditCustomerUserModal, ResetPasswordModal }
  })
  export default class CustomerUserAccounts extends BaseComponent {

    searchText = "";

    @Ref("table") table: BTable;
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#",
        thClass: "text-nowrap",
      },
      {
        key: "name",
        label: this.$t("fullName").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "username",
        label: "Username",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "phone",
        label: this.$t("phoneNumber").toString(),
        thClass: "text-nowrap",
      },
      {
        key: "email",
        label: "Email",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "role",
        label: this.$t("role").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
        formatter: value => ({
          Customer: this.$t("roleAdmin"),
          User: this.$t("user"),
          View: this.$t("viewOnly"),
        }[value])
      },
      {
        key: "createdAt",
        label: this.$t("createdDate").toString(),
        thClass: "text-nowrap",
        /*formatter: value => moment(value).format("DD/MM/YYYY")*/
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      }
    ];

    itemsPerPageSelections = [
      { value: 10, text: "10 " + this.$t("itemsPerPage").toString() },
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;

    userToEdit = null;


    mounted() {

    }


    onClickReloadBtn() {
      this.searchText = "";
      this.currentPage = 1;
      this.table.refresh();
    }

    async onClickDownload() {
      try {
        let customers = []; 
        customers = await userService.getAllUserForExport();
        
        const sheetData = customers.map(it => [
          it.name, it.username, it.phone, it.email
        ]);
        sheetData.unshift(["Họ và tên", "Username", "Di động", "Email", "Mật khẩu mặc định"]);

        const XLSX = await import("xlsx");

        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet);
        XLSX.writeFile(workbook, "DanhSachTaiKhoan.xlsx");
      } catch (e) {
        console.error(e);
        this.toastError(e);
      }
    }

    async onclickDeleteAllViewer() {
      const modalMessage = this.$t("deleteAllViewerConfirmation").toString() ;
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });
      if (confirmDelete) {
        try {
          await userService.deleteAllViewer();
          this.toastSuccessDelay(this.$t("deleteAllViewerSuccess").toString());

          this.table.refresh();
        } catch (e) {
          console.error(e);
          this.toastError(e);
        }
      }
    }

    onClickSearch() {
      this.table.refresh();
    }

    refreshData() {
      this.table.refresh();
    }


    onClickEditCustomerUser(user) {
      this.userToEdit = user;
      this.$bvModal.show("edit-customer-user-modal");

    }

    onClickResetPassword(user) {
      this.userToEdit = user;
      this.$bvModal.show("reset-password-modal");
    }


    async onClickDeleteUser(user) {
      const modalMessage = this.$t("deleteAccountConfirmation", { account: user.name }).toString() + `(ID: ${user.id})?`;
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          await userService.deleteUser(user.id);
          this.refreshData();
          this.toastSuccessDelay(this.$t("deleteAccountSuccess").toString());

        } catch (e) {
          this.toastError(e);
        }
      }
    }


    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      try {
        const res = await userService.getCustomerUsers({
          page: ctx.currentPage,
          pageSize: this.itemsPerPage,
          q: this.searchText?.trim(),
        });

        this.totalItems = res.total;
        return res.data;

      } catch (e) {
        this.toastError(e);
        return [];
      }
    }

  }
</script>
